/**
 * Soft header vocabulary → W8 seat contract.
 *
 * Soft UI used numeric seats + invented GPIO labels. The server uses HDR-N /
 * BUS-* and never invents gpio. This adapter is the only place that maps.
 */

/** Soft role chip id → server ROLE_CLASS key. Bus Soft chips are refused. */
export const SOFT_ROLE_TO_SERVER = {
  pir: "pir",
  door: "door_contact",
  tamper: "tamper",
  temp: "temp_humidity",
  vibe: "pulse_counter",
  siren: "siren",
  gate: "door_strike",
  pa: "pa_speaker",
};

/** Server roles offered in the live binder (execute high-risk flagged). */
export const SERVER_ROLES = [
  { id: "pir", label: "PIR motion", cls: "sense", risk: null },
  { id: "door_contact", label: "Door contact", cls: "sense", risk: null },
  { id: "window_contact", label: "Window contact", cls: "sense", risk: null },
  { id: "tamper", label: "Tamper", cls: "sense", risk: null },
  { id: "smoke", label: "Smoke", cls: "sense", risk: null },
  { id: "water_leak", label: "Water leak", cls: "sense", risk: null },
  { id: "panic_button", label: "Panic button", cls: "sense", risk: null },
  { id: "temp_humidity", label: "Temp / humidity", cls: "sense", risk: null },
  { id: "analog_in", label: "Analogue in", cls: "sense", risk: null },
  { id: "pulse_counter", label: "Pulse / vibration", cls: "sense", risk: null },
  { id: "siren", label: "Siren relay", cls: "execute", risk: "high" },
  { id: "strobe", label: "Strobe", cls: "execute", risk: null },
  { id: "relay", label: "Relay", cls: "execute", risk: null },
  { id: "door_strike", label: "Door strike", cls: "execute", risk: "high" },
  { id: "gate_drive", label: "Gate drive", cls: "execute", risk: "high" },
  { id: "pa_speaker", label: "Public address", cls: "execute", risk: "high" },
];

export function seatIdFromSoftSeat(seatNum) {
  const n = Number(seatNum);
  if (!Number.isFinite(n) || n < 1) return "";
  return "HDR-" + n;
}

export function softSeatFromSeatId(seatId) {
  const m = /^HDR-(\d+)$/i.exec(String(seatId || "").trim());
  return m ? Number(m[1]) : null;
}

export function toServerRole(softOrServerId) {
  const id = String(softOrServerId || "").trim();
  if (!id) return null;
  if (SERVER_ROLES.some((r) => r.id === id)) return id;
  return SOFT_ROLE_TO_SERVER[id] || null;
}

export function roleMeta(serverRole) {
  return SERVER_ROLES.find((r) => r.id === serverRole) || null;
}

/** Merge W8 GET /pins items onto Soft geometry pins for the 3D stage. */
export function mergeLiveSeatsOntoSoftPins(softPins, liveItems) {
  const bySeat = new Map();
  for (const s of liveItems || []) {
    const n = softSeatFromSeatId(s.seat_id || s.id);
    if (n != null) bySeat.set(n, s);
  }
  return (softPins || []).map((p) => {
    const live = bySeat.get(Number(p.seat));
    if (!live) {
      return {
        ...p,
        seat_id: seatIdFromSoftSeat(p.seat),
        liveBoard: false,
        assignable: false,
        gpio: null,
        evidence: "device_required",
        version: null,
      };
    }
    const role = live.role || live.bound_role || null;
    const meta = role ? roleMeta(role) : null;
    const state = String(live.state || live.binding_state || "unassigned");
    return {
      ...p,
      seat_id: String(live.seat_id || seatIdFromSoftSeat(p.seat)),
      liveBoard: true,
      assignable: live.assignable !== false && live.gpio != null,
      gpio: live.gpio == null ? null : Number(live.gpio),
      evidence: live.evidence || "",
      version: live.version != null ? Number(live.version) : null,
      role: role,
      fn: meta ? meta.label : (live.function_label || role || null),
      cls: meta ? meta.cls : (live.seat_class || p.cls),
      state,
      probe: live.gpio == null ? "gpio null · waiting for pinmap" : (live.probe || p.probe),
      risk: meta ? meta.risk : null,
      pinmap_reported: true,
    };
  });
}

export function explainBoardError(err) {
  const e = String(err || "");
  if (/device_board_disabled|503/.test(e)) {
    return "Board surface is off for this deployment (DEVICE_BOARD_ENABLED). Flip the flag — do not Soft-write pins.";
  }
  if (/board_profile_unknown|409/.test(e) && /board|profile/i.test(e)) {
    return "This node has not reported which board it is. Wait for the node report, or seed a virtual board profile for QA — the server never invents a pin map.";
  }
  if (/board_profile_unknown/.test(e)) {
    return "This node has not reported which board it is. Wait for the node report — the server never invents a pin map.";
  }
  if (/seat_gpio_unreported/.test(e)) {
    return "This seat has no GPIO yet. Run learn or wait for the device pinmap report — the server never invents a number.";
  }
  if (/seat_not_assignable|seat_unknown/.test(e)) {
    return "That seat cannot take a role (bus/power/unknown).";
  }
  if (/binding_version_conflict/.test(e)) {
    return "Someone else re-pointed this seat. Reload and confirm again.";
  }
  if (/unsupported_role/.test(e)) {
    return "That role is not in the W8 ROLE_CLASS table.";
  }
  return e || "Board write refused";
}
